import React, { useEffect, useState } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  Button,
  Box,
  Heading,
  Image,
  Text,
  InputGroup,
  InputRightElement,
  Link,
} from "@chakra-ui/react";
import { FiEye, FiEyeOff } from "react-icons/fi";
import UserCreate from "./UserCreate";
import AmplifyService from "../services/amplify";
import * as Sentry from "@sentry/react";
import { useAlert } from "./AlertProvider";

interface LoginModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const generateReferenceCode = () => {
  return Math.random().toString(36).substring(2, 15);
};

const logError = (error: any, errorInfo: any = null) => {
  const referenceCode = generateReferenceCode();
  Sentry.captureException(error, { extra: { ...errorInfo, referenceCode } });
  console.error(`Error logged with reference code: ${referenceCode}`, error, errorInfo);
  return referenceCode;
};

logError.info = (message: string, info: any) => {
  console.info(message, info);
};

logError.error = (message: string, error: any) => {
  logError(new Error(message), { error });
};

const LoginModal: React.FC<LoginModalProps> = ({ isOpen, onClose }) => {
  useEffect(() => {
    setViewState(MAINP);
  }, [isOpen]);

  const MAINP = 0;
  const LOGIN = 1;
  const MFA = 2;
  const RESET = 3;
  const NEWPASSWORD = 4;

  const loginService = new AmplifyService();

  // const [newPassword, setNewPassword] = useState("");
  // const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [resetMessage, setResetMessage] = useState("");
  const [viewState, setViewState] = useState(MAINP);
  const [showLoginError, setShowLoginError] = useState(false);
  const [loginError, setLoginError] = useState("");

  const [buttonText, setButtonText] = useState("Login");
  const [MFAValue, setMFAValue] = useState("");

  const [user, setUser] = useState({});
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const [show, setShow] = useState(false);
  const handleClick = () => setShow(!show);

  // eslint-disable-next-line
  const [issOpen, setIsOpen] = useState(false);
  const [showUserCreateModal, setShowUserCreateModal] = useState(false);

  const [loading, setLoading] = useState(false); // State to track loading status

  const { showAlert } = useAlert()

  const handleCreateAccount = () => {
    setShowUserCreateModal(true);
  };

  const handleCloseUserCreateModal = () => {
    setShowUserCreateModal(false);
  };

  const doClose = () => {
    setIsOpen(false);
  };

  // eslint-disable-next-line
  const [isModalOpen, setModalOpen] = useState(false);

  // const openModal = () => setModalOpen(true);
  // eslint-disable-next-line
  const closeModal = () => setModalOpen(false);

  // eslint-disable-next-line
  const onOpen = () => setIsOpen(true);

  const LoginAgain = () => {
    setShowLoginError(false);
    setMFAValue("");
    setLoginError("");
    // setNewPassword("");
    // setConfirmNewPassword("");

    switch (viewState) {
      default:
      case MFA:
        setViewState(LOGIN);
        break;
      case NEWPASSWORD:
        setViewState(RESET);
        break;
    }
  };

  useEffect(() => {
    if (isOpen) {
      // Send a message to the parent window to scroll to the top
      window.parent.postMessage({ action: 'scrollToTop' }, '*');
    }
  }, [isOpen]);

  const showUI = () => {
    switch (viewState) {
      case MAINP:
        return mainp();
      case LOGIN:
        return showLogin();
      case MFA:
        return showMFA();
      case RESET:
        return showReset();
      case NEWPASSWORD:
        return showNewPassword();
      default:
        return <>No value set</>;
    }
  };

  const mainp = () => {
    return (
      <span>
        <Text fontSize="md">Select your choice to login your account.</Text>
      </span>
    );
  };

  const showLogin = () => {
    return (
      <span>
        <Text fontSize="md">Enter the details below to login to your account.</Text>
        <FormControl mb={4} mt={8}>
          <FormLabel>Email</FormLabel>
          <Input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Enter your email"
            borderRadius="30px"
          />
        </FormControl>
        <FormControl mb={4}>
          <FormLabel>Password</FormLabel>
          <InputGroup size="md">
            <Input
              pr="4.5rem"
              type={show ? "text" : "password"}
              placeholder="Enter password"
              borderRadius="30px"
              onChange={(e) => setPassword(e.target.value)}
            />
            <InputRightElement width="4.5rem">
              <Button h="1.75rem" size="sm" onClick={handleClick} borderRadius="30px">
                {show ? <FiEyeOff /> : <FiEye />}
              </Button>
            </InputRightElement>
          </InputGroup>
        </FormControl>
        <Link alignSelf="center" onClick={onForgotPassword}>
          Forgot password
        </Link>
      </span>
    );
  };

  const showMFA = () => {
    return (
      <>
        <Text fontSize="md">Enter the MFA code to complete login.</Text>
        <FormControl mb={4} mt={8}>
          <FormLabel>Code</FormLabel>
          <Input
            type="number"
            value={MFAValue}
            onChange={(e) => setMFAValue(e.target.value)}
            placeholder="Enter your MFA code"
            borderRadius="30px"
          />
        </FormControl>
      </>
    );
  };

  const showReset = () => {
    return (
      <span>
        <Text fontSize="md">Enter the details below to reset to your password.</Text>
        <FormControl mb={4} mt={8}>
          <FormLabel>Email</FormLabel>
          <Input
            type="text"
            value={email}
            onChange={(e) => setEmail(e.target.value.toLowerCase())}
            placeholder="Enter your email"
            borderRadius="30px"
          />
        </FormControl>
      </span>
    );
  };

  const showNewPassword = () => {
    return (
      <span>
        {resetMessage}
        <Text>Please check your spam mail in case too.</Text>
        
      </span>
    );
  };

  const onForgotPassword = () => {
    setButtonText("Submit");
    setViewState(RESET);
  };

  const doLogin = () => {
    if (loading) return; // Prevent double click

    setLoading(true); // Set loading state to true

    loginService
      .signIn({
        username: email,
        password: password,

      })
      .then((result: any) => {
        if (result) {
          setUser(result);
          switch (result.challengeName) {
            case "SOFTWARE_TOKEN_MFA":
            case "SMS_MFA":
              setViewState(MFA);
              setEmail("");
              setPassword("");
              setShowLoginError(false);
              setLoginError("");
              setButtonText("Confirm");
              break;
            default:
            case "DONE":
              doClose();
              window.location.reload();
              break;
            case "NEW_PASSWORD_REQUIRED": {
              showAlert({
                severity: "info",
                title: "New Password Required",
                message: "You need to log into the web app to reset your password"
              });
            }
          }
        }
      })
      .catch((e) => {
        if (e.code === "NotAuthorizedException" && e.message === "Incorrect username or password.") {
          setLoginError("Incorrect username or password.");
        } else {
          const referenceCode = logError(e);
          setLoginError(`An error occurred. Please contact support with reference code: ${referenceCode}`);
        }

        setShowLoginError(true);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const doMFA = () => {
    if (!MFAValue) {
      setShowLoginError(true);
      setLoginError("Confirmation code cannot be empty");
      return;
    }
    const userAttributes = {};
    loginService
      .confirmSignIn(MFAValue, user, { userAttributes })
      .then((signInResult) => {
        if (signInResult.signInUserSession) {
          setButtonText("Login");
          setViewState(LOGIN);
          setMFAValue("");
          setEmail("");
          setPassword("");
          setShowLoginError(false);
          setLoginError("");
          setViewState(LOGIN);
          onClose();
          // GTAG EVENT
          (window as any).gtag('event', 'login', {
            method: "email_and_password"
          });
          window.location.reload(); // Reload the page after successful MFA
        } else {
          const err = signInResult.error.message;
          setLoginError(err);
          setShowLoginError(true);
        }
      });
  };


  const doPasswordReset = () => {
    loginService.forgotPassword(email).then((result: any) => {
      setViewState(NEWPASSWORD);
      setButtonText("Return");
      setResetMessage(`Password reset ${result.CodeDeliveryDetails.DeliveryMedium} has been sent to ${result.CodeDeliveryDetails.Destination}.`);
      // GTAG EVENT
      (window as any).gtag('event', 'auth', {
        event: 'password_reset_initiated'
      });
    }).catch((e) => {
    })
  }

  const doNewPassword = () => {

    setViewState(LOGIN);
    
  }

  const handleLogin = async () => {
    if (viewState === MAINP) {
      setViewState(LOGIN);
      setButtonText("Login");
    } else {
      switch (viewState) {
        case LOGIN:
          doLogin();
          break;
        case MFA:
          doMFA();
          break;
        case RESET:
          doPasswordReset();
          break;
        case NEWPASSWORD:
          doNewPassword();
          break;
      }
    }
  };



  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md" blockScrollOnMount={false} closeOnOverlayClick={false}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Login</ModalHeader>
        <ModalCloseButton />
        <ModalBody>

          <Box
            flex="1"
            display="block"
            justifyContent="center"
            alignItems="center"
          >
            <Image
              display="block"
              src="/appadd.jpg"
              alt="Dan Abramov"
              objectFit="cover"
            />
          </Box>

          <Box flex="1" p={4}>
            <Heading as="h2" size="md" noOfLines={1}>
              Welcome to Family Doctor
            </Heading>

            {showUI()}
            {showLoginError &&
              <>
                <Box mt={2} display={'Flex'}>
                  <div style={{ color: 'red', fontSize: '14px' }}>{loginError}</div>
                  <Button colorScheme='blackAlpha' borderRadius={30} size="xs" onClick={LoginAgain} ml={2}>Clear</Button> &nbsp;
                </Box>
              </>
            }
            <Box display="flex" alignItems="center">

              <Button
                bg="#B92F2F"
                color="#fff"
                onClick={handleLogin}
                mt={4}
                width="100%"
                borderRadius="30px"
              >
                {buttonText}
              </Button>
              </Box>

            {viewState !== LOGIN && viewState !== MFA && viewState !== RESET && (
              <>
                <Text fontSize="md" mt={5} textAlign="center">
                  or
                </Text>

                <Box display="flex" alignItems="center">
                  <Button
                    border="2px solid #B92F2F"  
                    color="#B92F2F"  
                    bg="transparent"
                    mt={4}
                    width="100%"
                    borderRadius="30px"
                    onClick={handleCreateAccount}
                    mb={4}
                  >
                    Create an Account
                  </Button>
                  </Box>
                {showUserCreateModal && <UserCreate isOpen={true} onClose={handleCloseUserCreateModal} />}

              
              </>
            )}
          </Box>


        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default LoginModal;